import { AssistantRuntimeProvider, useAuiState } from '@assistant-ui/react';
import { AssistantChatTransport, useChatRuntime } from '@assistant-ui/react-ai-sdk';
import {
  AssistantActionBar,
  AssistantMessage,
  BranchPicker,
  Composer,
  Thread,
} from '@assistant-ui/react-ui';
import '@assistant-ui/react-ui/styles/index.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { App, Button, Card, Drawer, Flex } from 'antd';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import { useQueryParams } from '@/common/hooks/use-query-params';
import { useAppContext } from '@/common/hooks/AppStateProvider';
import { MarkdownText } from '@/common/components/MarkdownText';
import CSSGrid from '@/common/components/CSSGrid';
import { AI_AVATAR } from '@/common/utils/ai-avatar';
import { useThemePreference } from '@/common/theme/ThemeContext';
import AgentChatThreadList from '../components/AgentChatThreadList';
import AgentResultPanel from '../components/AgentResultPanel';
import AgentWelcome from '../components/AgentWelcome';
import AgentReasoningMessage, {
  AgentReasoningContent,
  AgentReasoningFinishContext,
} from './AgentReasoningMessage';
import { recordReasoningFinish } from '../utils/reasoning-state';
import { clearCreationQuery, getCreationRecordQuery } from '../utils/creation-record';
import { getAgentsChatApi, queryAgentChatMessages } from '../services/chat';

const AGENT_WORKSPACE_CARD_HEIGHT = 'calc(100vh - 128px)';
const SEED_TYPE_TEXT = {
  hotPot: '基于热点继续创作',
  seedPlan: '基于规划继续创作',
};
const isRestorableUIMessage = (message) => {
  if (!message || typeof message !== 'object') {
    return false;
  }
  return Boolean(message.id && message.role && Array.isArray(message.parts));
};
const isRestorableUIMessages = (messages) =>
  Array.isArray(messages) && messages.length > 0 && messages.every(isRestorableUIMessage);
const toExternalMessageRepository = (messages) => ({
  headId: messages.at(-1)?.id,
  messages: messages.map((message, index) => ({
    message,
    parentId: index === 0 ? null : messages[index - 1].id,
  })),
});
const prepareIncrementalChatRequest = (options) => {
  const latestMessage = options.messages.at(-1);
  return {
    api: options.api,
    body: {
      ...options.body,
      id: options.id,
      messages: latestMessage ? [latestMessage] : [],
      trigger: options.trigger,
      messageId: options.messageId,
      metadata: options.requestMetadata,
    },
    credentials: options.credentials,
    headers: options.headers,
  };
};

const createPendingEntryArtifact = ({ record, seedId, seedTitle, seedType }) => {
  if (!seedId || !seedType) {
    return undefined;
  }
  return {
    data: record || { id: seedId, title: seedTitle },
    entityId: seedId,
    entityType: seedType,
    id: `pending-${seedType}-${seedId}`,
    title: seedTitle,
  };
};

const AgentThreadReasoningFallback = () => {
  const shouldShow = useAuiState((state) => {
    if (!state.thread.isRunning) {
      return false;
    }
    return state.thread.messages.at(-1)?.role !== 'assistant';
  });

  if (!shouldShow) {
    return null;
  }

  return <AgentReasoningContent status={{ type: 'running' }} />;
};

const AgentAssistantMessage = () => (
  <AssistantMessage.Root>
    <AssistantMessage.Avatar />
    <AssistantMessage.Content
      components={{
        Reasoning: AgentReasoningMessage,
      }}
    />
    <Flex vertical gap="small" style={{ gridColumn: 'span 2 / span 2' }}>
      <BranchPicker />
      <AssistantActionBar />
    </Flex>
  </AssistantMessage.Root>
);

export default function LegacyAgentsChat() {
  const { organizationId } = useOrganizationPath();
  const { session } = useAppContext();
  const { themeName } = useThemePreference();
  const [query, setQuery] = useQueryParams();
  const { message: feedback } = App.useApp();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [pendingEntryArtifact, setPendingEntryArtifact] = useState();
  const [threadRefreshKey, setThreadRefreshKey] = useState(0);
  const [reasoningFinishes, setReasoningFinishes] = useState(() => new Map());
  const loadedThreadRef = useRef();
  const threadId = Array.isArray(query.threadId) ? query.threadId.at(-1) : query.threadId;
  const handleThreadSelect = useCallback(
    (record) => {
      if (!record?.id) {
        return;
      }
      try {
        setQuery(
          { ...clearCreationQuery(query), ...getCreationRecordQuery(record) },
          { reset: true },
        );
        setPendingEntryArtifact(undefined);
        setHistoryOpen(false);
      } catch (error) {
        feedback.error(error.message);
      }
    },
    [feedback, query, setQuery],
  );
  const handleThreadRemove = useCallback(
    (record) => {
      if (record?.id && record.id === threadId) {
        setPendingEntryArtifact(undefined);
        setQuery(clearCreationQuery(query), { reset: true, replace: true });
      }
      setThreadRefreshKey((value) => value + 1);
    },
    [query, setQuery, threadId],
  );
  const api = useMemo(() => {
    return getAgentsChatApi(organizationId, threadId);
  }, [organizationId, threadId]);
  const handleChatFinish = useCallback(
    (result = {}) => {
      setReasoningFinishes((previous) => recordReasoningFinish(previous, result));
      const { isAbort, isDisconnect, isError, message } = result;
      if (isAbort || isDisconnect || isError) {
        return;
      }
      const nextThreadId = message?.metadata?.custom?.threadId;
      if (nextThreadId) {
        setQuery({ threadId: nextThreadId }, { replace: true });
      }
      setThreadRefreshKey((value) => value + 1);
    },
    [setQuery],
  );

  const runtime = useChatRuntime({
    onFinish: handleChatFinish,
    transport: new AssistantChatTransport({
      api,
      credentials: 'include',
      prepareSendMessagesRequest: prepareIncrementalChatRequest,
    }),
  });

  const handleEntryStart = useCallback(
    ({ record, seedId, seedTitle, seedType }) => {
      if (!seedId || !seedType || !runtime?.thread?.append) {
        return;
      }
      setPendingEntryArtifact(createPendingEntryArtifact({ record, seedId, seedTitle, seedType }));
      const prefix = SEED_TYPE_TEXT[seedType] || '开始创作';
      runtime.thread.append({
        role: 'user',
        content: [{ type: 'text', text: seedTitle ? `${prefix}：《${seedTitle}》` : prefix }],
        metadata: {
          custom: {
            seedId,
            seedType,
          },
        },
      });
    },
    [runtime],
  );

  useEffect(() => {
    if (!runtime?.thread) {
      return undefined;
    }
    if (!threadId) {
      if (loadedThreadRef.current && typeof runtime.thread.reset === 'function') {
        runtime.thread.reset([]);
        loadedThreadRef.current = undefined;
      }
      return undefined;
    }
    if (typeof runtime.thread.importExternalState !== 'function') {
      return undefined;
    }
    let ignore = false;
    queryAgentChatMessages({ organizationId, threadId })
      .then((messages) => {
        if (ignore || loadedThreadRef.current === threadId || !isRestorableUIMessages(messages)) {
          return;
        }
        runtime.thread.importExternalState(toExternalMessageRepository(messages));
        loadedThreadRef.current = threadId;
      })
      .catch(() => {});
    return () => {
      ignore = true;
    };
  }, [organizationId, runtime, threadId]);

  const ThreadWelcomeComponent = useCallback(
    () => <AgentWelcome onEntryStart={handleEntryStart} organizationId={organizationId} />,
    [handleEntryStart, organizationId],
  );
  const threadConfig = useMemo(
    () => ({
      assistantAvatar: AI_AVATAR,
      assistantMessage: {
        components: {
          Empty: AgentReasoningContent,
          Text: MarkdownText,
        },
      },
      components: {
        AssistantMessage: AgentAssistantMessage,
      },
      composer: { allowAttachments: false },
      strings: {
        welcome: { message: '欢迎使用智能体，内容创作从这里开始' },
        composer: {
          input: {
            placeholder: '输入你的创作想法，开始创作吧…',
          },
        },
      },
    }),
    [],
  );

  return (
    <AgentReasoningFinishContext.Provider value={reasoningFinishes}>
      <CSSGrid columns={2} gap="middle">
        <Card
          extra={
            <Button type="link" onClick={() => setHistoryOpen(true)}>
              创作记录
            </Button>
          }
          title="创作助手"
          variant="borderless"
          styles={{
            body: {
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
            },
            root: {
              display: 'flex',
              flexDirection: 'column',
              height: AGENT_WORKSPACE_CARD_HEIGHT,
              maxHeight: AGENT_WORKSPACE_CARD_HEIGHT,
            },
          }}
        >
          <AssistantRuntimeProvider runtime={runtime}>
            <Thread.Root className={themeName === 'dark' ? 'dark' : ''} config={threadConfig}>
              <Thread.Viewport scrollToBottomOnInitialize={false} turnAnchor="top">
                <ThreadWelcomeComponent />
                <Thread.Messages components={threadConfig.components} />
                <AgentThreadReasoningFallback />
                <Thread.FollowupSuggestions />
                <Thread.ViewportFooter>
                  <Thread.ScrollToBottom />
                  <Composer />
                </Thread.ViewportFooter>
              </Thread.Viewport>
            </Thread.Root>
          </AssistantRuntimeProvider>
        </Card>
        <AgentResultPanel
          organizationId={organizationId}
          pendingEntryArtifact={pendingEntryArtifact}
          threadId={threadId}
        />
      </CSSGrid>
      <Drawer
        destroyOnHidden
        open={historyOpen}
        title="创作记录"
        size="large"
        closable={{ placement: 'end' }}
        onClose={() => setHistoryOpen(false)}
      >
        <AgentChatThreadList
          currentRole={session?.role}
          onThreadRemove={handleThreadRemove}
          onThreadSelect={handleThreadSelect}
          organizationId={organizationId}
          refreshKey={threadRefreshKey}
        />
      </Drawer>
    </AgentReasoningFinishContext.Provider>
  );
}
